"use client";

import Image from "next/image";
import { Suspense } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { parseAsStringLiteral, useQueryState } from "nuqs";
import { ArrowRight, Check } from "lucide-react";
import {
  DEMO_TEMPLATE_IDS,
  DEMO_TEMPLATES,
} from "@ocr-monorepo/shared/demo-templates";
import { Button } from "@ocr-monorepo/ui/components/button";
import {
  RadioGroup,
  RadioGroupItem,
} from "@ocr-monorepo/ui/components/radio-group";
import { Label } from "@ocr-monorepo/ui/components/label";
import { cn } from "@/lib/utils";

export default function DemoTemplateSelectPage() {
  return (
    <Suspense>
      <DemoTemplatePicker />
    </Suspense>
  );
}

function DemoTemplatePicker() {
  const router = useRouter();
  const [selected, setSelected] = useQueryState(
    "template",
    parseAsStringLiteral(DEMO_TEMPLATE_IDS).withOptions({ history: "push" }),
  );
  const template = DEMO_TEMPLATES.find((item) => item.id === selected);
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <header className="flex items-center justify-between border-b border-border px-6 py-5 lg:px-12">
        <Link href="/" className="text-[23px] font-semibold leading-7">
          pdfparse
        </Link>
        <Link
          href="/"
          className="text-[13px] text-muted-foreground hover:text-foreground"
        >
          Exit demo setup
        </Link>
      </header>
      <main className="mx-auto max-w-[1536px] px-6 pb-8 pt-7 lg:px-12">
        <div className="mb-6 flex items-end justify-between gap-5">
          <div>
            <h1 className="text-[30px] font-semibold leading-9">
              Pick your demo space.
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Choose a starting point, then try your own documents.
            </p>
          </div>
          <p className="hidden shrink-0 text-xs text-muted-foreground sm:block">
            6 DEMO SPACES
          </p>
        </div>
        <RadioGroup
          aria-label="Demo template"
          value={selected ?? ""}
          onValueChange={(value) => void setSelected(value as typeof selected)}
          className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {DEMO_TEMPLATES.map((item) => (
            <Label
              key={item.id}
              htmlFor={`demo-${item.id}`}
              className={cn(
                "relative flex cursor-pointer flex-col overflow-hidden rounded-md border bg-card has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2",
                selected === item.id
                  ? "border-primary ring-1 ring-inset ring-primary"
                  : "border-border hover:border-primary/50",
              )}
            >
              <RadioGroupItem
                id={`demo-${item.id}`}
                value={item.id}
                aria-label={item.name}
                className="absolute inset-0 z-10 h-full w-full border-0 opacity-0"
              />
              <Image
                src={`/media/demos/${item.id}-doodles-v2.webp`}
                alt={`${item.name} sample document`}
                width={1672}
                height={941}
                className="h-[240px] w-full object-cover"
                sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                priority={item.id === "bank-statements"}
              />
              <div className="flex min-h-[120px] flex-col gap-2 p-5">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-lg font-semibold leading-6">
                    {item.name}
                  </span>
                  {selected === item.id && (
                    <Check
                      aria-hidden="true"
                      className="size-4 shrink-0 text-primary"
                    />
                  )}
                </div>
                <p className="text-sm font-normal leading-[22px] text-muted-foreground">
                  {item.description}
                </p>
              </div>
            </Label>
          ))}
        </RadioGroup>
        <footer className="flex flex-col justify-between gap-5 pt-6 sm:flex-row sm:items-center">
          <p className="text-[13px] text-muted-foreground" aria-live="polite">
            Selected{" "}
            <span className="ml-4 text-[15px] font-semibold text-foreground">
              {template?.name ?? "Choose a demo space"}
            </span>
          </p>
          <div className="flex items-center gap-6">
            <p className="text-[13px] text-muted-foreground">
              Next: review your demo space
            </p>
            <Button
              disabled={!selected}
              onClick={() => selected && router.push(`/demo/${selected}`)}
              className="px-7"
            >
              Continue <ArrowRight className="size-4" />
            </Button>
          </div>
        </footer>
      </main>
    </div>
  );
}
